// MaskPanel — 旧 Java applet `MaskSetCanvas.java` (50x32 極小 canvas) 風の
// **2 slot + 下部 type toggle** UI.
//
// Phase 6.7 R12 (consult 2026-05-05): 旧版 UI を完全踏襲.
//
//   ┌──────┬──────┐  ← 上半分: 2 slot (左 = slot1, 右 = slot2)
//   │ slot1│ slot2│    値未設定 (= null) → 黒枠 + ✕
//   │      │      │    値設定済 → その色で塗り + 黒枠
//   ├──────┴──────┤  ← 下半分: 1 行 toggle (灰=MASK, 黒=逆MASK)
//   │  MASK / 逆  │    クリックで normal ↔ inverse トグル
//   └─────────────┘
//
// クリック挙動:
//   - slot 1 クリック → 現在の `pen.color` を slot にコピー
//   - 同 slot 再クリック → 未設定 (null) に戻す
//   - 下部 click → `'normal' ↔ 'inverse'` トグル
//   - **両 slot 未設定なら mask 無効化** (= protocol `type='none'` に倒す, 旧版踏襲)
//
// protocol との整合 (`mask: { type, colors }`):
//   - slot1 / slot2 (number | null) は ClassicLayout 側で state 管理.
//   - `slotsToMask(slot1, slot2, typeUI)` で wire-level に変換.
//   - palette swatch は廃止 — ARGB スライダで自由色を選んで slot に流せる
//     ようになり, 旧版の 16 色制限より自由度向上.
//
// e2e selector (新):
//   - aria-label `マスク色 1` / `マスク色 2` (slot ボタン)
//   - aria-label `マスク切替` (下部 toggle)
// e2e selector (旧, 廃止): `マスク色 #rrggbb` / なし-ラジオ / マスク-ラジオ /
// 逆マスク-ラジオ. 既存 e2e に該当 selector は無いことを `grep` で確認済.
//
// (architecture.md §16 / classic-ui-restore.md §4 R12 / §6 確定方針)

import type { PenConfig } from '../../../domain/PenConfig.js';
import { insetFrame, outsetFrame } from './legacyFrames.js';
import type { MaskSlotValue, MaskTypeUI } from './maskHelpers.js';

const toCss = (rgb: number): string => `#${(rgb & 0xffffff).toString(16).padStart(6, '0')}`;

export interface MaskPanelProps {
  readonly pen: PenConfig;
  /** 各 slot の現在値 (`null` = 未設定 = ✕ 表示). */
  readonly slot1: MaskSlotValue;
  readonly slot2: MaskSlotValue;
  /** UI が保持する type (`'normal' | 'inverse'`, `'none'` は派生で出さない). */
  readonly typeUI: MaskTypeUI;
  /**
   * slot click. caller (ClassicLayout) は内部で
   *   `null → pen.color`, `非null → null` の toggle を行う.
   */
  readonly onSlotClick: (slot: 0 | 1) => void;
  /** 下部 toggle. caller は `'normal' ↔ 'inverse'` を切替. */
  readonly onTypeToggle: () => void;
}

export const MaskPanel = ({
  pen,
  slot1,
  slot2,
  typeUI,
  onSlotClick,
  onTypeToggle,
}: MaskPanelProps): React.ReactNode => {
  void pen; // pen.color は ClassicLayout 側で onSlotClick の中で使う想定. ここでは
  //          表示には使わない (slot にコピー済の値だけ表示).

  return (
    <fieldset
      aria-label="マスク"
      data-manual="color.mask"
      // ユーザ確認 2026-05-05: 外周枠 (outsetFrame) は維持しつつ p-0 + bg-white で
      // 旧版 MaskSetCanvas (50x32, BG_COLOR=Color.white) の見た目に寄せる. 内側の
      // slot / 下部 toggle は枠線にぴったり詰まる旧版踏襲。w-[50px] で ARGB Canvas
      // (50px) と幅統一, self-center で 60px 左サイドバー内中央寄せ.
      className={`flex w-[50px] flex-col self-center bg-white p-0 ${outsetFrame}`}
    >
      {/* legend は a11y 専用に sr-only 化. 旧版 MaskSetCanvas は上部に "MASK" の
          文字ラベルを持たず, 下部 toggle ボタン自身が "MASK" / "逆MASK" を表示する.
          視覚的には消すが fieldset の意味付けとして名前は保持. */}
      <legend className="sr-only">MASK</legend>

      {/* 上半分: 2 slot を 下部 MASK toggle 幅に合わせて等間隔配置 (旧版踏襲).
          flex justify-around で「親幅を 2 等分しそれぞれの中央に slot」となる
          (旧版 slot1 中央 x=12 / slot2 中央 x=37 = DSP_W=50 を 2 等分の中央).
          p-1 で slot 周囲に白マージン (旧版 g.drawRect(3, 3, ...) の 3px 余白
          踏襲, ユーザ確認 2026-05-05). */}
      <div className="flex justify-around p-1">
        <MaskSlotButton slot={0} value={slot1} onClick={() => onSlotClick(0)} />
        <MaskSlotButton slot={1} value={slot2} onClick={() => onSlotClick(1)} />
      </div>

      {/* 下半分: 1 行 toggle. typeUI=='normal' = 灰背景 / 'inverse' = 黒背景. */}
      <button
        type="button"
        aria-label="マスク切替"
        aria-pressed={typeUI === 'inverse'}
        onClick={onTypeToggle}
        // hover でオレンジ (旧版 mouseMoved 踏襲, 切替先プレビュー).
        // focus ring 抑制は旧 Java applet UI 完全再現要求.
        className={`relative flex h-5 items-center justify-center whitespace-nowrap text-[10px] font-bold focus:outline-none focus-visible:outline-none ${insetFrame} ${
          typeUI === 'inverse'
            ? 'bg-black text-white hover:bg-[var(--color-accent)]'
            : 'bg-gray-400 text-white hover:bg-[var(--color-accent)]'
        }`}
      >
        {typeUI === 'inverse' ? '逆MASK' : 'MASK'}
      </button>
    </fieldset>
  );
};

/**
 * Slot 1 個分のボタン. 値未設定 (null) は黒枠 + ✕ (対角線 2 本) を SVG で描画.
 * 値設定済はその色で塗り + 黒枠. クリックで親の onSlotClick(slot) を呼ぶ.
 */
const MaskSlotButton = ({
  slot,
  value,
  onClick,
}: {
  readonly slot: 0 | 1;
  readonly value: MaskSlotValue;
  readonly onClick: () => void;
}): React.ReactNode => {
  const empty = value === null;
  // aria-label は slot 番号ベース (`マスク色 1` / `マスク色 2`).
  // 1-based に揃えると旧版 GUI のラベル感に近い (slot=0 → 「1」).
  const label = `マスク色 ${slot + 1}`;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={!empty}
      onClick={onClick}
      // relative は ✕ SVG 重ね用. 静的 1px 黒 border は旧版踏襲 (色被り判別).
      // focus ring は旧 Java applet UI 完全再現要求により抑制 (識別は ✕ / 色塗りで代替).
      // h-[18px] は旧版 MaskSetCanvas VALUE_H=12 と前案 24 の中間 (ユーザ確認 2026-05-05).
      // w-6 (24px) はわずかに広め (旧版 VALUE_W=18 だが 50px 幅 fieldset に 2 slot
      // 並べると窮屈なので 24 を採用).
      className="relative flex h-[18px] w-6 items-center justify-center border border-black focus:outline-none focus-visible:outline-none"
      style={{ background: empty ? '#ffffff' : toCss(value) }}
      title={empty ? `${label}: 未設定 (クリックでペン色をコピー)` : `${label}: クリックで解除`}
    >
      {empty ? (
        // 未設定 = 黒対角線 ✕ で「色なし」を視覚化 (旧版 MaskSetCanvas.java 踏襲).
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="100%"
          height="100%"
          className="pointer-events-none absolute inset-0"
        >
          <line x1="0" y1="0" x2="24" y2="24" stroke="#000" strokeWidth="2" />
          <line x1="24" y1="0" x2="0" y2="24" stroke="#000" strokeWidth="2" />
        </svg>
      ) : null}
    </button>
  );
};
